import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GET_CAMPAIGN } from '../graphql/queries';
import Error from '../components/Error';
import CampaignNotFound from '../pages/Main/components/CampaignNotFound';
import ICampaign from './interfaces/ICampaign';
import useDemoCampaign from '../utils/hooks/demoCampaign';

interface ICampaignContext {
  campaign: ICampaign;
  loading: boolean;
}

const campaignWrapperDefaults = {
  campaign: {
    _id: '',
    slug: '',
    name: '',
    headerImgUrl: '',
    textConfig: {
      info: '',
      sellingDisabled: '',
      sellerGroupLabel: '',
      scoreboard: '',
      variantUnavailable: '',
      confirmationMail: '',
      memberMail: '',
    },
    openGraphTags: {
      title: '',
      description: '',
      imageUrl: '',
    },
    articles: [],
    deliveryOptions: {
      deliveryMethods: [],
      centralAddress: {
        description: '',
        coordinates: [],
      },
      radius: 0,
      minimumTotalPrice: 0,
    },
    organisation: {
      locale: '',
      _id: '',
      email: '',
      name: '',
      userId: '',
      permissions: [],
    },
    formFields: [],
    minimumTotalArticleCount: 0,
    minimumOrderValue: 0,
    isSellerGroupRequired: false,
    isSellingEnabled: true,
    showScoreboard: false,
    winnerCount: 3,
    acceptDonations: false,
    interfaceConfig: {
      sortFeatures: false,
      hideArticleCounter: false,
    },
    timeslots: {
      collect: [],
      deliver: [],
      eatIn: [],
    },
    onlyFinalOrderAllowed: false,
  },
  loading: false,
};

export const CampaignContext = React.createContext<ICampaignContext>(campaignWrapperDefaults);

const CampaignWrapper = ({ children } : { children: React.ReactNode; }) => {
  const { slug } = useParams();
  const [, i18n] = useTranslation();
  const [search] = useSearchParams();
  const queryParam = search.get('lang');
  const [lang, setLang] = useState('nl');
  const { campaign: demoCampaign, loading: demoCampaignLoading } = useDemoCampaign() || {};

  // ?cs=true is appended to skip the rewrites set in the trooper-webflow project
  // Once this project is reached successfully, the query param can be removed
  if (search.has('cs')) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.delete('cs');
    window.history.pushState({}, '', url);
  }

  const { data, loading, error } = useQuery(GET_CAMPAIGN, {
    skip: !slug || slug === 'demo',
    variables: { slug },
    onCompleted(d) {
      const targetLocale = queryParam || d?.campaignBySlug?.organisation?.locale;
      if (targetLocale) {
        i18n.changeLanguage(targetLocale);
        Dinero.globalLocale = targetLocale;
        setLang(targetLocale.substring(0, 2));
      }
    },
  });

  const campaign = demoCampaign || data?.campaignBySlug;

  const value = useMemo(() => ({
    campaign, loading: loading || demoCampaignLoading,
  }), [demoCampaignLoading, campaign, loading]);

  if (error) return <Error />;

  if (!campaign && !loading) return <CampaignNotFound />;

  return (
    <CampaignContext.Provider value={value}>
      <Helmet>
        <html lang={lang} />
      </Helmet>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignWrapper;
